import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

import { callClaude } from '../claude'

function CompetitorLibrary({ session, profile, onNavigate, initialCompetitorId }) {
  const [competitors, setCompetitors] = useState([])
  const [frameworks, setFrameworks] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [enriching, setEnriching] = useState(false)
  const [error, setError] = useState(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [activeTab, setActiveTab] = useState('profile')

  const emptyForm = {
    name: '', size: '', website_url: '', sector_focus: '',
    strengths: '', weaknesses: '', typical_pricing: '',
    key_personnel: '', ghost_strategy: '', notes: '', is_active: true
  }
  const [form, setForm] = useState(emptyForm)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll() }, [])

  useEffect(() => {
    if (initialCompetitorId && competitors.length > 0) {
      const c = competitors.find(x => x.id === initialCompetitorId)
      if (c) setSelected(c)
    }
  }, [initialCompetitorId, competitors])

  const loadAll = async () => {
    if (!profile?.organisation_id) return
    setLoading(true)
    const [{ data: comps }, { data: fws }] = await Promise.all([
      supabase.from('competitors').select('*').eq('organisation_id', profile?.organisation_id).order('name'),
      supabase.from('frameworks').select('id, name, reference_code').eq('organisation_id', profile?.organisation_id).eq('is_active', true).order('name')
    ])
    setCompetitors(comps || [])
    setFrameworks(fws || [])
    setLoading(false)
  }

  const loadSelected = async (comp) => {
    const { data: cf } = await supabase
      .from('competitor_frameworks')
      .select('id, framework_id, notes, frameworks(id, name, reference_code)')
      .eq('competitor_id', comp.id)
    setSelected({ ...comp, competitorFrameworks: cf || [] })
    setActiveTab('profile')
  }

  const handleEnrichProfile = async () => {
    if (!selected) return
    setEnriching(true)
    try {
      const system = `You are an expert in Government and Defence procurement markets in the UK. Generate a structured intelligence profile for a competitor organisation. Be factual, commercially focused, and based on publicly available information. Respond in JSON only.`

      const prompt = `Generate a competitive intelligence profile for this organisation.

Company: ${selected.name}
${selected.size ? 'Size: ' + selected.size : ''}
${selected.website_url ? 'Website: ' + selected.website_url : ''}
${selected.sector_focus ? 'Known sector focus: ' + selected.sector_focus : ''}

Based on your knowledge of this organisation and the UK Government and Defence procurement market, provide:

{
  "market_position": "2-3 sentences on their position in the G&D market",
  "known_strengths": ["strength 1", "strength 2", "strength 3"],
  "known_weaknesses": ["weakness 1", "weakness 2"],
  "typical_approach": "How they typically bid and position",
  "key_frameworks": ["framework 1", "framework 2"],
  "notable_wins": "Recent notable contract wins if known",
  "pricing_tendency": "Premium, competitive, or lowest price tendency",
  "key_differentiators": ["differentiator 1", "differentiator 2"],
  "watch_points": ["thing to watch 1", "thing to watch 2"],
  "confidence": "High" | "Medium" | "Low"
}`

      const result = await callClaude({ system, prompt, max_tokens: 1000 })
      const clean = result.replace(/```json/g, '').replace(/```/g, '').trim()
      const jsonMatch = clean.match(/\{[\s\S]*\}/)
      if (!jsonMatch) throw new Error('No JSON in response')
      const parsed = JSON.parse(jsonMatch[0])

      const { data } = await supabase
        .from('competitors')
        .update({ ai_enrichment: parsed })
        .eq('id', selected.id)
        .select()
        .single()

      if (data) {
        const updated = { ...selected, ai_enrichment: parsed }
        setSelected(updated)
        setCompetitors(prev => prev.map(c => c.id === selected.id ? updated : c))
      }
    } catch (err) {
      console.error('Enrich error:', err)
    }
    setEnriching(false)
  }

  const resetForm = () => { setForm(emptyForm); setShowAdd(false); setEditing(null); setError(null) }

  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true); setError(null)
    const payload = {
      name: form.name.trim(), size: form.size || null, website_url: form.website_url || null,
      sector_focus: form.sector_focus || null, strengths: form.strengths || null,
      weaknesses: form.weaknesses || null, typical_pricing: form.typical_pricing || null,
      key_personnel: form.key_personnel || null, ghost_strategy: form.ghost_strategy || null,
      notes: form.notes || null, is_active: form.is_active
    }
    if (editing) {
      const { error } = await supabase.from('competitors').update(payload).eq('id', editing.id)
      if (error) { setError(error.message); setSaving(false); return }
      const updated = { ...editing, ...payload, competitorFrameworks: editing.competitorFrameworks }
      setCompetitors(prev => prev.map(c => c.id === editing.id ? updated : c))
      setSelected(updated)
    } else {
      const { data, error } = await supabase.from('competitors').insert([{ ...payload, organisation_id: profile?.organisation_id, created_by: session.user.id }]).select().single()
      if (error) { setError(error.message); setSaving(false); return }
      setCompetitors(prev => [...prev, { ...data, competitorFrameworks: [] }].sort((a, b) => a.name.localeCompare(b.name)))
    }
    setSaving(false); resetForm()
  }

  const handleEdit = (comp) => {
    setForm({ name: comp.name || '', size: comp.size || '', website_url: comp.website_url || '', sector_focus: comp.sector_focus || '', strengths: comp.strengths || '', weaknesses: comp.weaknesses || '', typical_pricing: comp.typical_pricing || '', key_personnel: comp.key_personnel || '', ghost_strategy: comp.ghost_strategy || '', notes: comp.notes || '', is_active: comp.is_active !== false })
    setEditing(comp); setShowAdd(true)
  }

  const handleDelete = async (id) => {
    await supabase.from('competitors').delete().eq('id', id)
    setCompetitors(prev => prev.filter(c => c.id !== id))
    if (selected?.id === id) setSelected(null)
  }

  const handleAddFramework = async (frameworkId) => {
    if (!selected) return
    if (selected.competitorFrameworks?.find(cf => cf.framework_id === frameworkId)) return
    const { data, error } = await supabase.from('competitor_frameworks').insert([{ competitor_id: selected.id, framework_id: frameworkId }]).select('id, framework_id, notes, frameworks(id, name, reference_code)').single()
    if (!error && data) {
      const updated = { ...selected, competitorFrameworks: [...(selected.competitorFrameworks || []), data] }
      setSelected(updated)
      setCompetitors(prev => prev.map(c => c.id === selected.id ? updated : c))
    }
  }

  const handleRemoveFramework = async (cfId) => {
    await supabase.from('competitor_frameworks').delete().eq('id', cfId)
    const updated = { ...selected, competitorFrameworks: selected.competitorFrameworks.filter(cf => cf.id !== cfId) }
    setSelected(updated)
    setCompetitors(prev => prev.map(c => c.id === selected.id ? updated : c))
  }

  const Field = ({ label, value }) => value ? (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '0.9375rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{value}</div>
    </div>
  ) : null

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>
      <div className="page-header">
        <p className="label">Intelligence</p>
        <h1>Competitive Intelligence</h1>
        <p>Build and maintain competitor profiles for your organisation. Tag competitors to frameworks and opportunities.</p>
      </div>
      {error && <div className="error-msg">{error}</div>}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

        {/* Left list */}
        <div style={{ width: '260px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-dark)' }}>{competitors.length} Competitor{competitors.length !== 1 ? 's' : ''}</div>
            <button className="btn btn--primary" style={{ padding: '5px 14px', fontSize: '0.8125rem' }} onClick={() => { resetForm(); setShowAdd(true); setSelected(null) }}>+ Add</button>
          </div>
          {loading && <p style={{ color: 'var(--slate)', fontSize: '0.875rem' }}>Loading...</p>}
          {competitors.map(comp => (
            <div key={comp.id} onClick={() => loadSelected(comp)}
              style={{ padding: '12px 16px', marginBottom: '4px', background: selected?.id === comp.id ? 'var(--green-pale)' : 'var(--white)', border: '1px solid ' + (selected?.id === comp.id ? 'var(--green)' : 'var(--green-light)'), borderLeft: '4px solid ' + (comp.is_active ? 'var(--green)' : 'var(--silver)'), cursor: 'pointer' }}
              onMouseEnter={e => { if (selected?.id !== comp.id) e.currentTarget.style.background = 'var(--green-pale)' }}
              onMouseLeave={e => { if (selected?.id !== comp.id) e.currentTarget.style.background = 'var(--white)' }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--charcoal)' }}>{comp.name}</div>
              {comp.size && <div style={{ fontSize: '0.75rem', color: 'var(--slate)', marginTop: '2px' }}>{comp.size}</div>}
            </div>
          ))}
          {!loading && competitors.length === 0 && <div className="card"><p style={{ color: 'var(--slate)', fontSize: '0.875rem', textAlign: 'center', padding: '16px 0' }}>No competitors yet.</p></div>}
        </div>

        {/* Right detail */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Add/Edit form */}
          {showAdd && (
            <div className="card">
              <div className="card__header"><span className="card__title">{editing ? 'Edit — ' + editing.name : 'New Competitor'}</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {[
                  { label: 'Company Name *', key: 'name', span: true, placeholder: 'e.g. Capita plc' },
                  { label: 'Size', key: 'size', placeholder: 'e.g. 50,000+ employees, £3bn revenue' },
                  { label: 'Website URL', key: 'website_url', placeholder: 'https://www.example.com' },
                  { label: 'Sector Focus', key: 'sector_focus', placeholder: 'e.g. Central Government, Defence, NHS' },
                  { label: 'Typical Pricing Approach', key: 'typical_pricing', placeholder: 'e.g. Lowest price, premium positioning' },
                  { label: 'Key Personnel', key: 'key_personnel', placeholder: 'Bid Director, Account Leads' },
                ].map(f => (
                  <div key={f.key} className="form-group" style={f.span ? { gridColumn: '1 / -1' } : {}}>
                    <label>{f.label}</label>
                    <input type="text" value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} />
                  </div>
                ))}
                {[
                  { label: 'Strengths', key: 'strengths', placeholder: 'What they do well...' },
                  { label: 'Weaknesses', key: 'weaknesses', placeholder: 'Where they are vulnerable...' },
                  { label: 'Ghost Strategy', key: 'ghost_strategy', placeholder: 'How they typically position against competitors like you...' },
                  { label: 'Notes', key: 'notes', placeholder: 'Any other intelligence...' },
                ].map(f => (
                  <div key={f.key} className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>{f.label}</label>
                    <textarea value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} rows={3} />
                  </div>
                ))}
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="checkbox" id="comp-active" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} style={{ width: 16, height: 16 }} />
                  <label htmlFor="comp-active" style={{ cursor: 'pointer', marginBottom: 0, fontWeight: 600 }}>Active competitor</label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button className="btn btn--primary" onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Competitor'}</button>
                <button className="btn btn--secondary" onClick={resetForm}>Cancel</button>
              </div>
            </div>
          )}

          {/* Detail view */}
          {selected && !showAdd && (
            <div>
              <div style={{ background: 'var(--green-dark)', padding: '20px 24px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.75rem', fontWeight: 600, color: 'var(--white)' }}>{selected.name}</div>
                    {selected.size && <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '4px' }}>{selected.size}</div>}
                    {selected.website_url && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--green-light)', marginTop: '4px', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.open(selected.website_url, '_blank')}>{selected.website_url}</div>
                    )}
                  </div>
                  <button onClick={() => handleEdit(selected)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'var(--silver)', padding: '6px 14px', fontSize: '0.75rem', cursor: 'pointer' }}>Edit</button>
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid var(--green-light)' }}>
                {['profile', 'frameworks'].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    style={{ padding: '8px 20px', background: activeTab === tab ? 'var(--green-dark)' : 'none', border: 'none', color: activeTab === tab ? 'var(--white)' : 'var(--slate)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', textTransform: 'capitalize' }}>
                    {tab === 'frameworks' ? 'Frameworks (' + (selected.competitorFrameworks?.length || 0) + ')' : 'Profile'}
                  </button>
                ))}
              </div>

              {activeTab === 'profile' && (
                <div className="card">
                  {selected.sector_focus && <Field label="Sector Focus" value={selected.sector_focus} />}
                  {selected.typical_pricing && <Field label="Typical Pricing Approach" value={selected.typical_pricing} />}
                  {selected.key_personnel && <Field label="Key Personnel" value={selected.key_personnel} />}
                  {selected.strengths && <Field label="Strengths" value={selected.strengths} />}
                  {selected.weaknesses && <Field label="Weaknesses" value={selected.weaknesses} />}
                  {selected.ghost_strategy && <Field label="Ghost Strategy" value={selected.ghost_strategy} />}
                  {selected.notes && <Field label="Notes" value={selected.notes} />}
                  {profile?.organisations?.ai_enabled && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                  <button
                    onClick={handleEnrichProfile}
                    disabled={enriching}
                    style={{ background: enriching ? 'var(--green-pale)' : 'none', border: '1px solid var(--green-dark)', color: 'var(--green-dark)', padding: '6px 16px', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', opacity: enriching ? 0.7 : 1 }}
                  >
                    {enriching ? '⏳ Enriching...' : '✦ AI Enrich Profile'}
                  </button>
                </div>
              )}
              {selected.ai_enrichment && (
                <div style={{ background: 'var(--green-dark)', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>AI Market Intelligence</div>
                    <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 8px', background: selected.ai_enrichment.confidence === 'High' ? 'var(--green)' : selected.ai_enrichment.confidence === 'Medium' ? '#E67E22' : 'var(--slate)', color: 'var(--white)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {selected.ai_enrichment.confidence} Confidence
                    </span>
                  </div>
                  {selected.ai_enrichment.market_position && (
                    <p style={{ fontSize: '0.875rem', color: 'var(--white)', lineHeight: 1.5, marginBottom: '12px' }}>{selected.ai_enrichment.market_position}</p>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {selected.ai_enrichment.known_strengths && (
                      <div>
                        <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Known Strengths</div>
                        {selected.ai_enrichment.known_strengths.map((s, i) => <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--white)', padding: '2px 0' }}>• {s}</div>)}
                      </div>
                    )}
                    {selected.ai_enrichment.known_weaknesses && (
                      <div>
                        <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Known Weaknesses</div>
                        {selected.ai_enrichment.known_weaknesses.map((w, i) => <div key={i} style={{ fontSize: '0.8125rem', color: '#E67E22', padding: '2px 0' }}>• {w}</div>)}
                      </div>
                    )}
                    {selected.ai_enrichment.key_differentiators && (
                      <div>
                        <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Key Differentiators</div>
                        {selected.ai_enrichment.key_differentiators.map((d, i) => <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--white)', padding: '2px 0' }}>• {d}</div>)}
                      </div>
                    )}
                    {selected.ai_enrichment.watch_points && (
                      <div>
                        <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Watch Points</div>
                        {selected.ai_enrichment.watch_points.map((w, i) => <div key={i} style={{ fontSize: '0.8125rem', color: '#E67E22', padding: '2px 0' }}>• {w}</div>)}
                      </div>
                    )}
                  </div>
                  {selected.ai_enrichment.typical_approach && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Typical Bidding Approach</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--white)', lineHeight: 1.5 }}>{selected.ai_enrichment.typical_approach}</div>
                    </div>
                  )}
                  {selected.ai_enrichment.notable_wins && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Notable Wins</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--white)', lineHeight: 1.5 }}>{selected.ai_enrichment.notable_wins}</div>
                    </div>
                  )}
                </div>
              )}
              {!selected.sector_focus && !selected.strengths && !selected.notes && (
                    <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '16px 0' }}>No profile details yet. Click Edit to add intelligence.</p>
                  )}
                </div>
              )}

              {activeTab === 'frameworks' && (
                <div>
                  <div className="card" style={{ marginBottom: '16px' }}>
                    <div className="card__header"><span className="card__title">Add to Framework</span></div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {frameworks.filter(fw => !selected.competitorFrameworks?.find(cf => cf.framework_id === fw.id)).map(fw => (
                        <button key={fw.id} onClick={() => handleAddFramework(fw.id)}
                          style={{ background: 'none', border: '1px solid var(--green)', color: 'var(--green)', padding: '4px 12px', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--green)'; e.currentTarget.style.color = 'var(--white)' }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--green)' }}
                        >
                          + {fw.reference_code || fw.name}
                        </button>
                      ))}
                      {frameworks.filter(fw => !selected.competitorFrameworks?.find(cf => cf.framework_id === fw.id)).length === 0 && (
                        <p style={{ color: 'var(--slate)', fontSize: '0.875rem' }}>All frameworks already linked.</p>
                      )}
                    </div>
                  </div>
                  {(selected.competitorFrameworks || []).length > 0 && (
                    <div className="card">
                      <div className="card__header"><span className="card__title">Linked Frameworks</span></div>
                      {selected.competitorFrameworks.map(cf => (
                        <div key={cf.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--green-light)' }}>
                          <div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--white)', background: 'var(--green-dark)', padding: '2px 8px', marginRight: '8px' }}>{cf.frameworks?.reference_code || ''}</span>
                            <span style={{ fontSize: '0.9375rem', color: 'var(--charcoal)', fontWeight: 600 }}>{cf.frameworks?.name}</span>
                          </div>
                          <button onClick={() => handleRemoveFramework(cf.id)}
                            style={{ background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '3px 10px', fontSize: '0.75rem', cursor: 'pointer' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                          >Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button onClick={() => handleDelete(selected.id)}
                  style={{ background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '8px 16px', fontSize: '0.8125rem', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                >Delete Competitor</button>
              </div>
            </div>
          )}

          {!selected && !showAdd && (
            <div className="card"><p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>Select a competitor or click <strong>+ Add</strong> to create your first profile.</p></div>
          )}
        </div>
      </div>
      <div className="actions" style={{ marginTop: '24px' }}>
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>← Dashboard</button>
      </div>
    </div>
  )
}

export default CompetitorLibrary